import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/website-conversion");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/website-conversion")({
  head: () => ({
    meta: [
      {
        title: "Why Your Website Gets Traffic But No Calls (And How to Fix It)",
      },
      {
        name: "description",
        content:
          "10 conversion killers we find on almost every Colorado small-business website — plus the exact fixes that turn visitors into booked jobs.",
      },
      {
        property: "og:title",
        content: "Website Traffic But No Calls? Here&rsquo;s Why",
      },
      {
        property: "og:description",
        content:
          "Honest, no-fluff conversion fixes from a 25-year Colorado web design agency.",
      },
      { property: "og:url", content: `${SITE_URL}/learn/website-conversion` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/website-conversion" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Web Design"
      read="9 min read"
      title="Why your website gets traffic but no calls (and how to fix it)"
      intro="Traffic without calls means one of two things: the wrong people are showing up, or the right people are showing up and your site is talking them out of calling. Here&rsquo;s the 10-point checklist we run on every Colorado small-business site."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Website conversion" },
      ]}
      path="/learn/website-conversion"
      faqs={[
        { q: "What is a good conversion rate for a service business website?", a: "2–5% of visitors becoming a lead (call, form, quote request) is solid. Top-performing Colorado service sites we run hit 6–10% with clear CTAs, fast load times, and trust signals above the fold." },
        { q: "Why is my website getting traffic but no calls?", a: "The three most common reasons: phone number isn't tap-to-call on mobile, the form is buried below the fold, or there are no trust signals (reviews, photos, guarantees) near the CTA." },
        { q: "Should every page have a call-to-action?", a: "Yes — every page should have at least one obvious next step (call, quote, book). If a visitor has to scroll or hunt to find how to contact you, you're losing leads." },
      ]}
    >
      <h2>The 10 conversion killers we find every time</h2>

      <h3>1. The phone number isn&rsquo;t click-to-call on mobile</h3>
      <p>
        70% of your visitors are on a phone. If your number isn&rsquo;t a tappable link in the
        header, you&rsquo;re losing calls every single day.
      </p>

      <h3>2. The hero doesn&rsquo;t say what you do, where, in 3 seconds</h3>
      <p>
        Visitors decide whether to stay in roughly 3 seconds. &ldquo;Welcome to our site&rdquo; is
        a death sentence. &ldquo;HVAC repair in Colorado Springs — same-day service, no overtime
        fees&rdquo; is a survivor.
      </p>

      <h3>3. There&rsquo;s no clear primary CTA</h3>
      <p>
        Every page should have one obvious next step. Most small-business sites have 4 buttons all
        styled the same — &ldquo;Learn more,&rdquo; &ldquo;Read our story,&rdquo; &ldquo;Schedule
        a consult,&rdquo; &ldquo;Call us.&rdquo; Pick one primary. Style it loud. Mute the rest.
      </p>

      <h3>4. The page is slow on a real phone</h3>
      <p>
        If your site takes 5+ seconds to load on a phone over LTE, half your visitors leave before
        they ever see the hero. Test your real Colorado mobile experience, not your Wi-Fi laptop
        speed.
      </p>

      <h3>5. The forms are too long</h3>
      <p>
        Every field you add cuts conversion by ~10%. Name, phone, &ldquo;what do you need
        help with?&rdquo; — that&rsquo;s the form. You&rsquo;re not building a CRM, you&rsquo;re
        getting a phone call.
      </p>

      <h3>6. There&rsquo;s no proof anywhere above the fold</h3>
      <p>
        Star rating, total review count, &ldquo;serving Colorado since 1999,&rdquo; logos of
        chambers/associations. Real proof, not stock badges.
      </p>

      <h3>7. The pricing page says &ldquo;Contact us&rdquo;</h3>
      <p>
        In 2026, that&rsquo;s a hard exit for most buyers. Show ranges, packages, or a calculator.
        Ours is on{" "}
        <Link to="/pricing" className="font-bold text-brand-gold-deep underline">
          /pricing
        </Link>{" "}
        and{" "}
        <Link to="/pricing" className="font-bold text-brand-gold-deep underline">
          /pricing
        </Link>{" "}
        — for the same reasons we&rsquo;re telling you to do the same.
      </p>

      <h3>8. Service pages all read the same</h3>
      <p>
        If you swap the H1, your AC repair page is identical to your furnace page. Both Google and
        humans see right through it. Each service deserves real, specific copy.
      </p>

      <h3>9. No live trust signals</h3>
      <p>
        Recent reviews pulled from Google, recent jobs gallery with city + date, &ldquo;serving X
        cities, Y customers, Z years.&rdquo; Live, current, dated.
      </p>

      <h3>10. The contact page is a wall of fields</h3>
      <p>
        Phone number first, big and tappable. Form second. Map third. Hours fourth. Don&rsquo;t
        bury the way they actually want to reach you.
      </p>

      <h2>How to actually test it</h2>
      <p>
        Here&rsquo;s the test we run on every site we audit:
      </p>
      <ol>
        <li>Open your site on a phone, on cellular, not Wi-Fi.</li>
        <li>Time how long until the hero is readable. Over 3 seconds = problem.</li>
        <li>Read the hero out loud. Could a stranger tell what you do and where in one breath?</li>
        <li>Tap the phone number. Does it dial? If not — that&rsquo;s leak #1.</li>
        <li>Scroll once. Is there proof on screen? A star rating, a review, a years-in-business badge?</li>
        <li>Find the primary CTA. Is it the most visible button on the page? Or is it competing with 4 other buttons styled the same?</li>
      </ol>

      <h2>What to do next</h2>
      <p>
        These 10 fixes turn most sites into real lead generators inside 30 days — no full rebuild
        required. If you want a full review, our{" "}
        <Link to="/colorado-springs-web-design" className="font-bold text-brand-gold-deep underline">
          Web Design service
        </Link>{" "}
        starts with a free conversion audit by Chris (the owner), not a salesperson, not a bot. Or
        keep reading — see{" "}
        <Link to="/learn/mobile-website-mistakes" className="font-bold text-brand-gold-deep underline">
          7 mobile website mistakes
        </Link>{" "}
        for the phone-specific issues we see most.
      </p>
    </ArticleLayout>
  );
}
